import * as at from './actionTypes'
import * as jobLevels from "./JobLevels"
import _ from "lodash"

const initialState = {
  jobTypes: [],
  jobTypesByJobLevel: {},
  jobTypesById: {},
  jobTypesLoading: true,
  selectedJobType: undefined,
  selectedJobLevel: undefined,
  providers: [],
  query: {},
  queryDisplay: {},
  queryValid: false,
  isQueryExecuted: false,
  entriesLoading: true,
  queueJobsRequested: false,
  queueJobsSummary: undefined,
  entries: [],
  page: 0,
  pageSize: 8,
  selectedCount: 0
}

const withComputedJobTypeFields = (state) => {
  const byLevel = {};
  state.jobTypes.forEach( jobType => {
    byLevel[jobType.jobLevel] = (byLevel[jobType.jobLevel] || []);
    byLevel[jobType.jobLevel].push(jobType)
  });
  const byId = {};
  state.jobTypes.forEach( jobType => {
    byId[jobType.id] = jobType
  });
  return {
    ...state,
    jobTypesByJobLevel: byLevel,
    jobTypesById: byId,
  }
}

const clearJobQueuedResults = initialState => state => {
  const didQueryChange = initialState.selectedJobLevel !== state.selectedJobLevel || (
      !_.isEqual(initialState.query, state.query)
    );
  return withIsQueryValid({
    ...state,
    isQueryExecuted: didQueryChange ? false : state.isQueryExecuted,
    page: didQueryChange ? 0 : state.page,
    queueJobsRequested: false,
    queueJobsSummary: undefined
  })
}

const requirements = {
  [jobLevels.AccountKey]: q => {
    return (q.accountIds && q.accountIds.length)
  },
  [jobLevels.LocationKey]: q => {
    return (q.accountIds && q.accountIds.length) ||
    (q.locationIds && q.locationIds.length) ||
    (q.userIds && q.userIds.length)
  },
  [jobLevels.LocationProviderKey]: q => {
    return q.provider && (
      (q.accountIds && q.accountIds.length) ||
      (q.locationIds && q.locationIds.length) ||
      (q.userIds && q.userIds.length)
      )
  },
  [jobLevels.GlobalKey]: q => {
    return true
  },
  [jobLevels.UserKey]: q => {
    return (q.accountIds && q.accountIds.length) ||
    (q.userIds && q.userIds.length)
  },
  [jobLevels.BillingAccountKey]: q => {
    return (q.billingAccountIds && q.billingAccountIds.length)
  },
  [jobLevels.CredentialKey]: q => {
    return (q.billingAccountIds && q.billingAccountIds.length) ||
      (q.accountIds && q.accountIds.length) ||
      (q.credentialIds && q.credentialIds.length)
  },
  [jobLevels.RemotePageKey]: q => {
    return (q.billingAccountIds && q.billingAccountIds.length) ||
      (q.accountIds && q.accountIds.length) ||
      (q.credentialIds && q.credentialIds.length) ||
      (q.locationIds && q.locationIds.length)
  }
}

const withIsQueryValid = (state) => {
  const requirement = requirements && requirements[state.selectedJobLevel]
  return {
    ...state,
    isQueryValid: !!(requirement && requirement(state.query))
  }
}

const setSelection = (state, indexes, selection) => {
  const updated = state.entries.slice();
  for (let i of indexes) {
    updated[i] = { ...updated[i], selected: selection }
  }
  return {
    ...state,
    entries: updated,
    selectedCount: updated.reduce((sum, e) => sum + (e.selected ? 1 : 0), 0)
  }
}

const reducers = {

  [at.SET_JOB_TYPES]: (state, action) => withComputedJobTypeFields({
    ...state,
    jobTypesLoading: false,
    jobTypes: action.jobTypes.sort((a, b) => a.id.localeCompare(b.id))
  }),

  [at.SET_QUERY]: (state, action) => clearJobQueuedResults(state)({
    ...state,
    query: {
      ...state.query,
      ...action.query,
    },
    queryDisplay: {
      ...state.queryDisplay,
      ...action.queryDisplay,
    }
  }),

  [at.SET_JOB_TYPE]: (state, action) => clearJobQueuedResults(state)({
    ...state,
    selectedJobType: action.jobType,
    selectedJobLevel: state.jobTypesById[action.jobType] && state.jobTypesById[action.jobType].jobLevel,
  }),

  [at.LOADING_ENTRIES]: (state, action) => ({...state, isQueryExecuted: true, entriesLoading: true}),

  [at.SET_ENTRIES]: (state, action) => ({
    ...state,
    entriesLoading: false,
    entries: action.entries.map((e, i) => ({...e, index: i, selected: true})),
    selectedCount: action.entries.length
  }),

  [at.SET_JOBS_QUEUED_PENDING]: (state, action) => ({...state, queueJobsRequested: true }),

  [at.SET_JOBS_QUEUED]: (state, action) => ({...state, queueJobsRequested: false, queueJobsSummary: action.summary}),

  [at.RESET]: () => initialState,

  [at.SET_ENTRIES_PAGE]: (state, action) => ({...state, page: action.page}),

  [at.SET_ALL_ITEMS_SELECTION]: (state, action) => setSelection(state, _.range(state.entries.length), action.selected),

  [at.SET_ITEMS_SELECTED]: (state, action) =>
    setSelection(state, action.items.map(x => x.index), true),

  [at.TOGGLE_ITEM]: (state, action) => setSelection(state, [action.item.index], !state.entries[action.item.index].selected),

  [at.CLEAR_QUEUED_STATUS]: (state, action) => ({...state, queueJobsSummary: undefined})
};

const sessKey = "manageToolRedux_0"
const getInitialState = () => {
  let serial = window.sessionStorage.getItem(sessKey)
  try {
    return JSON.parse(serial) || initialState
  } catch (e) {
    return initialState
  }
}

const persist = _.debounce((state) => {
  window.sessionStorage.setItem(sessKey, JSON.stringify(state));
}, 2000);


export function manageToolReducer(state = getInitialState(), action) {
  const fn = reducers[action.type]
  const nextState = fn ? fn(state, action) : state
  persist(nextState)
  return nextState
}
